import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = {
  key: 2,
  class: "prescription-rule"
}
const _hoisted_4 = { class: "rule-group" }
const _hoisted_5 = {
  key: 3,
  "data-test-id": "preceding-assignment-list"
}
const _hoisted_6 = {
  key: 0,
  class: "over-days-count"
}
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", null, "через", -1)
const _hoisted_8 = ["value", "readonly"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", null, "дней", -1)

import moment, { Moment } from 'moment';
import {
    computed,
    ref,
} from 'vue';
import {
    keyBy,
} from 'lodash';

import {
    IAssignment,
} from '@tools/types';

interface SelectOption {
    key: string;
    value: string;
    disabled: boolean;
    children: { children: string }[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PrescriptionRule',
  props: {
    disabled: { type: Boolean },
    assignment: null,
    patientAssignments: null,
    patientAssignmentsIndices: null,
    assignmentRules: null
  },
  emits: ["change-rule-code", "date-plan-change", "preceding-assignment-change", "over-days-count-change"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'change-rule-code',
        value: string,
        assignment: IAssignment,
    ): void;
    (
        e: 'date-plan-change',
        value: Moment | null,
        assignment: IAssignment,
    ): void;
    (
        e: 'preceding-assignment-change',
        value: number | string | null,
        assignment: IAssignment,
    ): void;
    (
        e: 'over-days-count-change',
        value: number | null,
        assignment: IAssignment,
    ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    disabled: boolean;
    assignment: IAssignment;
    patientAssignments: IAssignment[];
    patientAssignmentsIndices: Record<number | string, number>;
    assignmentRules: {
        code: string;
        name: string;
    }[];
}





const depsListIsActive = ref(false);
const precedingAssignmentList = ref();
const onPrecedingAssignmentListBlur = () => {
    depsListIsActive.value = false;
};
const onPrecedingAssignmentListMount = () => {
    precedingAssignmentList.value.focus();
};

const rulesListIsActive = ref(false);
const onRulesListBlur = () => {
    rulesListIsActive.value = false;
};

const displayValidationError = ref(false);
const validate = () => {
    displayValidationError.value = true;
};
const hasError = (value: Moment | string | number | null) => (
    displayValidationError.value && value === null ? 'has-error' : ''
);

expose({
    validate,
});

const assignmentRulesMap = computed(() => keyBy(props.assignmentRules, 'code'));
const getRuleName = (code: string) => {
    const rule = assignmentRulesMap.value[code];
    return rule ? rule.name : code;
};

const onRuleSelect = (value: string) => {
    emit(
        'change-rule-code',
        value,
        props.assignment,
    );
    onRulesListBlur();
};

const onPlannedDateSelect = (value: Moment | null) => emit(
    'date-plan-change',
    value,
    props.assignment,
);

const onPrecedingAssignmentSelect = (value: number | string | null) => {
    emit(
        'preceding-assignment-change',
        value,
        props.assignment,
    );
    onPrecedingAssignmentListBlur();
};

const onGapChange = (event: Event) => {
    let value = null;
    if (event?.target) {
        const target = event.target as HTMLInputElement;
        value = parseInt(target.value, 10);
        if (Number.isNaN(value)) {
            value = null;
        }
    }
    emit(
        'over-days-count-change',
        value,
        props.assignment,
    );
};

const getAssignmentId = (record: IAssignment) => (
    // TODO: pull changes in the li-tools and update projects
    record.id
        ? `id-${record.id}`
        // @ts-ignore
        : `localId-${record.localId}`
);

const getAssignmentIndex = (assignment: IAssignment) => {
    const id = getAssignmentId(assignment);
    const idx = props.patientAssignmentsIndices[id];
    return idx ? `${idx}. ` : '';
};

const patientAssignmentsMap = computed(() => keyBy(
    props.patientAssignments,
    getAssignmentId,
));

const getPrecedingAssignmentId = (assignment: IAssignment) => {
    if (assignment.overAssignmentId) {
        return `id-${assignment.overAssignmentId}`;
    }
    // @ts-ignore
    if (assignment.overAssignmentLocalId) {
        // @ts-ignore
        return `localId-${assignment.overAssignmentLocalId}`;
    }
    return null;
};

const getAssignmentPredecessorName = computed(() => {
    const prevId = getPrecedingAssignmentId(props.assignment);
    if (!prevId) {
        return '';
    }
    const prevAssignment = patientAssignmentsMap.value[prevId];
    if (!prevAssignment) {
        return '';
    }
    return `${getAssignmentIndex(prevAssignment)}${prevAssignment.name}`;
});

const filterOption = (input: string, option: SelectOption) => {
    const text = option.children?.[0]?.children;
    if (!text) {
        return false;
    }
    return text.toLowerCase().includes(input.toLowerCase());
};

const assignmentIsReachableByRecursion = (
    record: IAssignment,
    targetId: string,
): boolean => {
    if (!record) {
        return false;
    }
    // @ts-ignore
    if (!record.overAssignmentLocalId && !record.overAssignmentId) {
        return false;
    }

    const nextId = record.overAssignmentId
        ? `id-${record.overAssignmentId}`
        // @ts-ignore
        : `localId-${record.overAssignmentLocalId}`;
    if (nextId === targetId) {
        return true;
    }

    return assignmentIsReachableByRecursion(
        patientAssignmentsMap.value[nextId],
        targetId,
    );
};

const getPossiblePrecedingAssignments = (
    testField: 'clinicGoodId' | 'medicationCourse',
    assignment: IAssignment,
) => {
    const result = props.patientAssignments.filter((x) => !x.deleted
        && x[testField]
        && ((x.id && (x.id !== assignment.id))
        // @ts-ignore
        || (x.localId && (x.localId !== assignment.localId)))
        && !assignmentIsReachableByRecursion(x, getAssignmentId(assignment)));
    return result;
};

const dateIsDisabled = (current: Moment) => current <= moment().endOf('day').add(-1, 'days');

const getPopupContainer = (trigger: HTMLElement) => trigger.parentElement;

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_date_picker = _resolveComponent("a-date-picker")!

  return (__props.assignment.timeStarted)
    ? (_openBlock(), _createElementBlock("span", _hoisted_1, " Выполнение начато " + _toDisplayString(_unref(moment)(__props.assignment.timeStarted).format('DD.MM.YYYY')), 1))
    : (__props.assignment.cancellationComment)
      ? (_openBlock(), _createElementBlock("span", _hoisted_2, " Отменено "))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            (__props.disabled || !rulesListIsActive.value)
              ? (_openBlock(), _createBlock(_component_a_input, {
                  key: 0,
                  disabled: __props.disabled,
                  value: getRuleName(__props.assignment.ruleCode),
                  onFocus: _cache[0] || (_cache[0] = ($event: any) => (rulesListIsActive.value = true))
                }, null, 8, ["disabled", "value"]))
              : (_openBlock(), _createBlock(_component_a_select, {
                  key: 1,
                  "default-open": "",
                  "data-test-id": "prescription-rule-list",
                  value: __props.assignment.ruleCode,
                  "dropdown-match-select-width": false,
                  "get-popup-container": getPopupContainer,
                  disabled: __props.disabled,
                  placeholder: "Условие",
                  onSelect: onRuleSelect,
                  onBlur: onRulesListBlur
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.assignmentRules, (rule) => {
                      return (_openBlock(), _createBlock(_component_a_select_option, {
                        key: rule.code
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(rule.name), 1)
                        ]),
                        _: 2
                      }, 1024))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["value", "disabled"])),
            (__props.assignment.ruleCode === 'MANUAL_DATE')
              ? (_openBlock(), _createBlock(_component_a_date_picker, {
                  key: 2,
                  value: __props.assignment.datePlanStart,
                  "disabled-date": dateIsDisabled,
                  disabled: __props.disabled,
                  class: _normalizeClass(hasError(__props.assignment.datePlanStart)),
                  format: "DD.MM.YYYY",
                  "data-test-id": "prescription-rule-date-from",
                  onChange: onPlannedDateSelect
                }, null, 8, ["value", "disabled", "class"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  (__props.disabled || !depsListIsActive.value)
                    ? (_openBlock(), _createBlock(_component_a_input, {
                        key: 0,
                        placeholder: __props.assignment.ruleCode === 'AFTER_GOOD_CREATED'
                        ? 'Услуга'
                        : 'Препарат',
                        disabled: __props.disabled,
                        value: _unref(getAssignmentPredecessorName),
                        class: _normalizeClass(hasError(getPrecedingAssignmentId(__props.assignment))),
                        onFocus: _cache[1] || (_cache[1] = ($event: any) => (depsListIsActive.value = true))
                      }, null, 8, ["placeholder", "disabled", "value", "class"]))
                    : (_openBlock(), _createBlock(_component_a_select, {
                        key: 1,
                        ref_key: "precedingAssignmentList",
                        ref: precedingAssignmentList,
                        class: _normalizeClass(["preceding-assignment", hasError(getPrecedingAssignmentId(__props.assignment))]),
                        "default-open": "",
                        "dropdown-match-select-width": false,
                        "get-popup-container": getPopupContainer,
                        value: getPrecedingAssignmentId(__props.assignment),
                        "filter-option": filterOption,
                        placeholder: __props.assignment.ruleCode === 'AFTER_GOOD_CREATED'
                        ? 'Услуга'
                        : 'Препарат',
                        "show-search": "",
                        onSelect: onPrecedingAssignmentSelect,
                        onBlur: onPrecedingAssignmentListBlur,
                        onVnodeMounted: onPrecedingAssignmentListMount
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getPossiblePrecedingAssignments(
                            __props.assignment.ruleCode === 'AFTER_GOOD_CREATED'
                                ? 'clinicGoodId'
                                : 'medicationCourse',
                            __props.assignment,
                        ), (option) => {
                            return (_openBlock(), _createBlock(_component_a_select_option, {
                              key: getAssignmentId(option)
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(getAssignmentIndex(option)) + " " + _toDisplayString(option.name), 1)
                              ]),
                              _: 2
                            }, 1024))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["value", "class", "placeholder"]))
                ]))
          ]),
          (__props.assignment.ruleCode !== 'MANUAL_DATE')
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _hoisted_7,
                _createElementVNode("input", {
                  type: "number",
                  "data-test-id": "prescription-days-count",
                  value: __props.assignment.overDaysCount,
                  min: 0,
                  step: 1,
                  readonly: __props.disabled,
                  class: _normalizeClass(["over-days-count-input", hasError(__props.assignment.overDaysCount)]),
                  onChange: onGapChange
                }, null, 42, _hoisted_8),
                _hoisted_9
              ]))
            : _createCommentVNode("", true)
        ]))
}
}

})